import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import type { CurrencyExchangeRate } from "$routes/_lib/billing-v2/currency-exchange";
import { useMemo } from "react";
import {
  type CurrencyFormatter,
  currencyFormatFor,
} from "./currency-formatter";
import { penceToPounds } from "./use-convert-currency";

export type CurrencyExchangeFormat = {
  formatMoney: (currency: string, amountInMinorUnits: number) => string;
  currencySymbol: (currency: string) => string;
  formatRate: (
    rate: Pick<CurrencyExchangeRate, "baseCurrency" | "quoteCurrency" | "rate">,
  ) => string;
};

export const useCurrencyExchangeFormat = (): CurrencyExchangeFormat => {
  const billingCurrency = useBillingCurrency();

  return useMemo(() => {
    const formatters = new Map<string, CurrencyFormatter | undefined>();
    const formatterFor = (currency: string) => {
      if (!formatters.has(currency)) {
        formatters.set(
          currency,
          currencyFormatFor({ ...billingCurrency, code: currency }),
        );
      }
      return formatters.get(currency);
    };
    const currencySymbol = (currency: string) =>
      formatterFor(currency)
        ?.formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? currency;

    return {
      currencySymbol,
      formatMoney: (currency, amountInMinorUnits) => {
        const amount = penceToPounds(billingCurrency, amountInMinorUnits);
        return (
          formatterFor(currency)?.format(amount) ??
          `${currency} ${amount.toFixed(2)}`
        );
      },
      formatRate: ({ baseCurrency, quoteCurrency, rate }) =>
        `${currencySymbol(baseCurrency)}1 = ${currencySymbol(quoteCurrency)}${rate}`,
    };
  }, [billingCurrency]);
};
